import getConfig from 'next/config'

import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import {
  AddressLineFormat,
  AddressLookupOptions,
  CheckoutSettings,
  Routes,
} from '@grandvisionhq/checkout-v2'
import type { IntlContextModel } from '@grandvisionhq/state'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'

interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

export const basePath = '/checkout'
export const routes: Routes = {
  shipping: {
    label: 'Envio',
    path: `${basePath}/envio`,
  },
  overview: {
    label: 'Resumo',
    path: `${basePath}/resumo`,
  },
  payment: {
    label: 'Pagamento',
    path: `${basePath}/pagamento`,
  },
}

export const getCheckoutConfig = ({ cart, intl }: SettingsInput): CheckoutSettings => {
  const addressLookup: AddressLookupOptions = {
    addressLineFormat: AddressLineFormat.streetName_streetNumber,
    shouldCombineAddress: true,
  }

  const formFieldsMap = {
    billingAddress: [
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'streetNumber', required: true },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
      { field: 'region', required: true },
    ],
    deliveryAddress: [
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'streetNumber', required: true },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
      { field: 'region', required: true },
    ],
    contactDetails: [
      { field: 'email', required: true },
      { field: 'phone', required: true },
      { field: 'customerVatId', required: true },
    ],
    company: [
      { field: 'name', required: true },
      { field: 'customerVatId', required: true },
    ],
    invoice: [
      { field: 'insuranceProvider', required: false },
      { field: 'insuranceNumber', required: false },
    ],
  }
  return {
    formFieldsMap,
    account: {
      implicitAccountCreation: true,
      createAccountRequired: true,
    },
    consent: {
      items: [
        {
          title: `${intl.getFormattedLabel('checkout.consent.conditions', {
            parseLink: true,
            openLinksInNewTab: true,
            parseHTML: false,
          })} *`,
          value: 'terms-conditions',
          required: true,
          errorMessage: intl.getLabel('checkout.consent.conditions.error'),
        },
      ],
      implicitTermsAndConditions: false,
    },
    addressLookup,
    shippingDetails: {
      loadingFinished: Boolean(cart) || cart === null,
      ...(cart?.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart?.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart?.customerDetails ? { customer: cart.customerDetails } : {}),
    },
    shippingFormFields: {
      billingAddress: [
        'firstName',
        'lastName',
        'streetName',
        'additionalStreetInfo',
        'postalCode',
        'city',
        'region',
      ],
      contactDetails: ['email', 'phone', 'customerVatId'],
    },
    storeFinder: {
      showUseMyLocation: true,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: false,
      isServiceStoreEnabled: true,
    },
    login: {
      disableSignup: true,
      returnPath: cart ? `${routes.shipping?.path}?cartId=${cart.id}` : routes.shipping?.path,
    },
    routes,
    payments: {
      applePay: {
        merchantName: 'Multiopticas',
      },
    },
    logoVariant: 'main',
  }
}
